import { useMemo, useState } from 'react';
import { startCase } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import Upload from 'hooks/useDirectUpload/Upload';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { parseDate, simpleDashDate } from 'lib/date';
import Workflow, { ATTACHMENTS_VALUE_NAME, WorkflowAction } from 'models/Workflow';
import { createWorkflow, updateWorkflow, workflowQueryKeys, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall } from 'styles/typography';

import {
  DEMOGRAPHIC_FIELDS,
  INSURANCE_FIELDS,
  REFERRAL_INTAKE_API_NAME,
  ReferralFieldDef,
  VALUE_NAMES,
} from './constants';
import {
  COVERAGE_SEQUENCES,
  CoverageEntry,
  COVERAGES_VALUE,
  coveragesFromEntries,
  emptyCoverageEntries,
} from './coverages';
import { RestartConsentStep } from './RestartConsentModal';

// Which section the modal opens focused on. 'demographics' expands the demographics
// section (the "Edit demographics" entry point); 'documents' is the default packet view
// (the "Add additional documents" entry point). On create the demographics section is
// collapsed unless explicitly opened.
export type ReferralModalSection = 'documents' | 'demographics';

interface Props {
  // Present in edit mode; absent when creating.
  workflow?: Workflow;
  section?: ReferralModalSection;
  onClose: () => void;
}

type BuildingOption = { id: string; name: string };

// How a new referral is being created (UX, 2026-08-04). The two paths are alternatives, not
// steps: upload a packet and let extraction fill everything in, or type what you know. Only the
// typing path has a second step, because only it asks for insurance.
type CreateMethod = 'upload' | 'manual';

// Every value field across both sections; the date field is handled separately (dob state),
// the rest (text + select) share the `text` record.
const VALUE_FIELDS = [...DEMOGRAPHIC_FIELDS, ...INSURANCE_FIELDS];
const TEXT_FIELDS = VALUE_FIELDS.filter((field) => field.type !== 'date');

const sameIds = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

export default function ReferralModal({ workflow, section = 'documents', onClose }: Props) {
  const navigate = useNavigate();
  const isEdit = !!workflow;

  const [selectedBuildings, setSelectedBuildings] = useState<BuildingOption[]>(
    workflow?.groups.map((group) => ({ id: group.id, name: group.name })) ?? []
  );

  const stored = useMemo(() => {
    const text = Object.fromEntries(
      TEXT_FIELDS.map((field) => {
        return [field.name, workflow?.valueFor(field.name)?.value ?? ''];
      })
    );
    const dob = workflow?.valueFor(VALUE_NAMES.PATIENT_DOB)?.value ?? '';
    return { text, dob };
  }, [workflow]);
  const originalGroupIds = useMemo(() => workflow?.groups.map((group) => group.id) ?? [], [workflow]);

  const [text, setText] = useState<Record<string, string>>(stored.text);
  const [dob, setDob] = useState<Date | null>(parseDate(stored.dob) ?? null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // The impact list awaiting the user's consent (design §9) — non-null while the modal
  // is on its second (confirm) step. Going back clears it without saving; the form's
  // state lives here in the parent, so the person's edits are preserved.
  const [pendingRestart, setPendingRestart] = useState<WorkflowAction[] | null>(null);
  // Demographics start expanded in edit mode or when the modal was opened to edit them;
  // collapsed on a fresh create (upload is the primary path).
  const [demographicsOpen, setDemographicsOpen] = useState(isEdit || section === 'demographics');
  // Create only. Edit stays one form: its second step is already the restart consent, and
  // stacking a wizard on top of that would put two different "Back"s on the same modal.
  const [method, setMethod] = useState<CreateMethod>(section === 'demographics' ? 'manual' : 'upload');
  const [onInsuranceStep, setOnInsuranceStep] = useState(false);
  const [coverageEntries, setCoverageEntries] = useState<CoverageEntry[]>(emptyCoverageEntries);

  const { facilities: groups, isPending: groupsLoading } = useAllowedFacilities();
  // With access to exactly one building there's nothing to choose — assume it and hide the
  // selector. The selector only appears when a user can address more than one building.
  const onlyBuilding = !groupsLoading && groups.length === 1 ? groups[0] : null;

  const queryClient = useQueryClient();

  // Apply the settled response to the cache so the acting user sees their own change
  // immediately, rather than waiting for the next list/detail poll to reconcile it.
  const cacheWorkflow = (saved: Workflow) => {
    queryClient.setQueryData(workflowQueryKeys.show(saved.id), saved);
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.list });
  };

  const { mutate: create, isPending: creating } = useMutateWithToast({
    mutationFn: createWorkflow,
    onSuccess: (created: Workflow) => {
      cacheWorkflow(created);
      onClose();
      navigate(`/inbound-referrals/${created.id}`);
    },
  });
  const { mutate: save, isPending: saving } = useMutateWithToast({
    mutationFn: updateWorkflow,
    onSuccess: (updated: Workflow) => {
      cacheWorkflow(updated);
      onClose();
    },
  });

  const changedValues = (): WorkflowValuesInput => {
    const changes: WorkflowValuesInput = {};
    TEXT_FIELDS.forEach((field) => {
      const next = text[field.name].trim();
      if (next !== stored.text[field.name]) changes[field.name] = next;
    });
    const nextDob = simpleDashDate(dob) ?? '';
    if (nextDob !== stored.dob) changes[VALUE_NAMES.PATIENT_DOB] = nextDob;
    return changes;
  };

  // What the insurance step typed, as values. `coverages` is the whole typed set, which is the
  // shape extraction produces and the shape per-coverage checks will be derived from (OLIO-2793).
  //
  // The primary is ALSO mirrored to the run-level plan_type / member_id, because those are what
  // today's single payer search and benefit verification read. Once coverages are real those two
  // become per-coverage and the mirror goes away — until then, dropping it would mean a typed
  // primary never reaches the check it was typed for.
  //
  // The trap while it's one value: a typed set is a MANUAL write of `coverages`, and manual
  // outranks machine, so alongside a packet upload extraction's coverages would lose to this whole
  // set rather than merging into it — someone who typed only a primary would discard a secondary
  // the packet named.
  //
  // Two things keep them apart. The methods are exclusive, so switching clears the other side's
  // input — and the `uploads` check below is the backstop that states the invariant where the data
  // is assembled rather than trusting the UI wiring to enforce it. That backstop matters: this hole
  // has been found twice, once in each direction (manual→upload, then upload→manual), because each
  // fix guarded a route instead of the rule.
  //
  // OLIO-2793 dissolves it: a typed coverage becomes its own record, so extraction lands beside
  // it instead of being outranked by it, and both can be sent together safely.
  const insuranceValues = (): WorkflowValuesInput => {
    if (uploads.length > 0) return {};

    const coverages = coveragesFromEntries(coverageEntries);
    if (coverages.length === 0) return {};

    const primary = coverages.find((coverage) => coverage.sequence === 'primary');
    return {
      [COVERAGES_VALUE]: coverages,
      ...(primary?.payerName ? { [VALUE_NAMES.PLAN_TYPE]: primary.payerName } : {}),
      ...(primary?.memberId ? { [VALUE_NAMES.MEMBER_ID]: primary.memberId } : {}),
    };
  };

  const effectiveBuildings = onlyBuilding ? [{ id: onlyBuilding.id, name: onlyBuilding.name }] : selectedBuildings;
  const groupIds = effectiveBuildings.map((building) => building.id);
  const groupsChanged = isEdit && !sameIds(groupIds, originalGroupIds);
  const demographicChanges = changedValues();
  // Not gated on the visible panel: switching tabs hides a field, it doesn't discard what was
  // typed into it. Edit mode has its own insurance section and never reaches this.
  const pendingValues: WorkflowValuesInput = { ...demographicChanges, ...(isEdit ? {} : insuranceValues()) };
  const attachments = uploads.length > 0 ? uploads : undefined;
  const hasContent = Object.keys(pendingValues).length > 0 || !!attachments;
  const canSubmit = groupIds.length > 0 && (isEdit ? hasContent || groupsChanged : hasContent);

  const typingReferral = !isEdit && method === 'manual';
  const onInsurance = typingReferral && onInsuranceStep;
  // Insurance is optional, so Next is gated on the demographics — there has to be a patient
  // before there's anything to insure.
  const canAdvance = groupIds.length > 0 && Object.keys(demographicChanges).length > 0;

  // The steps this save would ripple into (design §9): the changed value names — a
  // packet upload is a change to the `attachments` value — walked through the declared
  // contracts. Empty on create and on terminal runs (the API rejects restarts there).
  const impactedActions = (): WorkflowAction[] => {
    if (!isEdit || workflow.isTerminal) return [];
    const changedNames = Object.keys(pendingValues);
    if (attachments) changedNames.push(ATTACHMENTS_VALUE_NAME);
    return changedNames.length > 0 ? workflow.impactedActionsBy(changedNames) : [];
  };

  const saveEdit = (restartActionIds?: string[]) => {
    if (!workflow) return;
    const values = Object.keys(pendingValues).length > 0 ? pendingValues : undefined;
    save({
      workflowId: workflow.id,
      groupIds: groupsChanged ? groupIds : undefined,
      values,
      attachments,
      restartActionIds,
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    if (isEdit) {
      // Consent-at-save (design §9, 002 §6): when the correction ripples into steps
      // that already ran, the person approves the restart list before anything saves.
      const impacted = impactedActions();
      if (impacted.length > 0) {
        setPendingRestart(impacted);
        return;
      }
      saveEdit();
      return;
    }
    const values = Object.keys(pendingValues).length > 0 ? pendingValues : undefined;
    create({ workflowDefinitionApiName: REFERRAL_INTAKE_API_NAME, groupIds, values, attachments });
  };

  const submitting = creating || saving;

  const updateCoverage = (index: number, key: keyof CoverageEntry, value: string) =>
    setCoverageEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [key]: value } : entry)));

  const renderField = (field: ReferralFieldDef) => (
    <Field key={field.name}>
      <FieldLabel>
        {field.label}
        {field.optional && <Optional> (optional)</Optional>}
      </FieldLabel>
      {field.type === 'date' && <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />}
      {field.type === 'select' && (
        <div data-testid={`field-${field.name}`}>
          <Select
            inputId={`field-${field.name}`}
            placeholder='Select'
            options={GENDERS}
            value={GENDERS.find((option) => option.value === text[field.name]) ?? null}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
      )}
      {field.type === 'text' && (
        <Input
          value={text[field.name]}
          onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
          data-testid={`field-${field.name}`}
        />
      )}
    </Field>
  );

  // Two-step wizard, one modal (no modal-on-modal): step 1 is the edit form; when the
  // save ripples into started steps, step 2 swaps the modal's content to the restart
  // consent. The form stays mounted (hidden) so Back restores the person's edits —
  // including the uncontrolled file input's queued uploads.
  const title = pendingRestart ? 'Restart affected steps?' : isEdit ? 'Edit Referral' : 'Create New Referral';

  // 560px is the mock's width (558). It has to be passed explicitly: StandardModal's default is
  // flex-basis 480px with 560px only as a max, and `flex: 0` means it never grows into that max —
  // so omitting this renders 480px, leaving 400px of content, and the paired fields (min-width
  // 200px + 16px gap = 416px) wrap to one per row.
  return (
    <StandardModal title={title} onCancel={onClose} maxWidth='560px'>
      {pendingRestart && (
        <RestartConsentStep
          actions={pendingRestart}
          saving={saving}
          onConfirm={() => saveEdit(pendingRestart.map((action) => action.id))}
          onBack={() => setPendingRestart(null)}
        />
      )}
      <Form $hidden={!!pendingRestart}>
        {/* Method first, then the facilities it applies to (UX, 2026-08-04). Hidden once the
            insurance step is showing: the method is settled by then, and Back is the way out of
            the step. Leaving it up would offer a second, unlabelled way to leave. */}
        {!isEdit && !onInsurance && (
          <div>
            <Lead>Select a method for creating a new referral.</Lead>
            <Methods>
              {(
                [
                  ['upload', 'Upload Referral Documents'],
                  ['manual', 'Manually Enter Information'],
                ] as [CreateMethod, string][]
              ).map(([value, label]) => (
                <MethodTab
                  key={value}
                  type='button'
                  $active={method === value}
                  aria-pressed={method === value}
                  onClick={() => {
                    if (value === method) return;
                    setMethod(value);
                    setOnInsuranceStep(false);
                    // The methods are alternatives, so each switch drops the other side's input:
                    // a packet has no place on the typing path, and typed coverages have no place
                    // alongside a packet (they'd outrank everything extraction finds). Both
                    // directions, because guarding only one is what let this through twice.
                    //
                    // Demographics deliberately survive either way: they're scalars, so a manual
                    // one overrides only itself, and typing a name alongside an upload has always
                    // been supported.
                    setCoverageEntries(emptyCoverageEntries);
                    setUploads([]);
                  }}
                  data-testid={`method-${value}`}>
                  {label}
                </MethodTab>
              ))}
            </Methods>
          </div>
        )}

        {groups.length > 1 && (
          <Field>
            <div data-testid='building-select'>
              <MultiSelectChips<BuildingOption>
                options={groups}
                values={selectedBuildings}
                onChange={setSelectedBuildings}
                getOptionValue={(building) => building.id}
                getOptionLabel={(building) => building.name}
                title='Select which facilities to associate to this referral'
                placeholder='Select'
              />
            </div>
          </Field>
        )}
        {!groupsLoading && groups.length === 0 && (
          <HelpText>You don’t have access to any buildings for inbound referrals.</HelpText>
        )}

        {/* Unmounted on the typing path, not merely hidden. The file input is uncontrolled, so
            hiding it would leave a queued file listed inside a component whose state we just
            cleared — the screen would disagree with what gets sent. Unmounting resets both. */}
        {!typingReferral && (
          <Panel>
            <Lead>
              <strong>Upload any associated referral files below.</strong> Olio will process and extract patient
              demographics and related referral information.
            </Lead>
            {workflow && workflow.attachments.length > 0 && (
              <CurrentPacket data-testid='current-packet'>
                Current packet:{' '}
                {workflow.attachments
                  .map((attachment) => attachment.filename)
                  .filter(Boolean)
                  .join(', ')}
              </CurrentPacket>
            )}
            <DropZone data-testid='packet-dropzone'>
              <FileInputComponent onChange={setUploads} onUploading={setIsUploading} renderUploadsInternally />
            </DropZone>
            {workflow && uploads.length > 0 && workflow.attachments.length > 0 && (
              <ReplaceWarning data-testid='packet-replace-warning'>
                Uploading will replace the current {workflow.attachments.length} document
                {workflow.attachments.length > 1 ? 's' : ''}.
              </ReplaceWarning>
            )}
          </Panel>
        )}

        {isEdit ? (
          <div>
            <Toggle
              type='button'
              onClick={() => setDemographicsOpen((open) => !open)}
              aria-expanded={demographicsOpen}
              data-testid='demographics-toggle'>
              ✎ Or manually add patient demographics {demographicsOpen ? '▴' : '▾'}
            </Toggle>
            {demographicsOpen && (
              <div data-testid='demographics-section'>
                <RerunNote data-testid='rerun-note'>
                  <strong>Note:</strong> Editing patient demographics or insurance information will re-run all completed
                  steps.
                </RerunNote>
                <Body>The AI fills these from the packet — edit them if needed.</Body>
                <SectionTitle>Patient Demographics</SectionTitle>
                <Fields>{DEMOGRAPHIC_FIELDS.map(renderField)}</Fields>
                <SectionTitle>Insurance Information</SectionTitle>
                <Fields>{INSURANCE_FIELDS.map(renderField)}</Fields>
              </div>
            )}
          </div>
        ) : (
          <>
            <Panel $hidden={!typingReferral || onInsuranceStep} data-testid='demographics-step'>
              <SectionTitle>Patient Demographics</SectionTitle>
              <Fields>{DEMOGRAPHIC_FIELDS.map(renderField)}</Fields>
            </Panel>

            <Panel $hidden={!onInsurance} data-testid='insurance-step'>
              <Lead>
                If available, provide the patient’s insurance information below. Olio will use this information to run a
                benefit verification check.
              </Lead>
              <CoverageNote data-testid='secondary-coverage-note'>
                Both coverages are saved with the referral. Olio verifies the primary one today — the secondary is
                captured now and gets its own check once per-coverage verification is turned on.
              </CoverageNote>
              {COVERAGE_SEQUENCES.map((sequence, index) => (
                <div key={sequence}>
                  <SectionTitle>{startCase(sequence)} Insurance</SectionTitle>
                  <StackedFields>
                    {(
                      [
                        ['payerName', 'Payer Name', 'payer-name'],
                        ['memberId', 'Member ID', 'member-id'],
                      ] as [keyof CoverageEntry, string, string][]
                    ).map(([key, label, testId]) => (
                      <div key={key}>
                        <FieldLabel>
                          {label}
                          <Optional> (optional)</Optional>
                        </FieldLabel>
                        <Input
                          value={coverageEntries[index][key]}
                          onChange={(e) => updateCoverage(index, key, e.target.value)}
                          data-testid={`coverage-${sequence}-${testId}`}
                        />
                      </div>
                    ))}
                  </StackedFields>
                </div>
              ))}
            </Panel>
          </>
        )}

        <Actions>
          {onInsurance ? (
            <Button variant='ghost' onClick={() => setOnInsuranceStep(false)} data-testid='back-to-demographics'>
              Back
            </Button>
          ) : (
            <Button variant='ghost' onClick={onClose} data-testid='cancel-referral'>
              Cancel
            </Button>
          )}
          {typingReferral && !onInsuranceStep ? (
            <Button onClick={() => setOnInsuranceStep(true)} disabled={!canAdvance} data-testid='next-step'>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting || isUploading}
              loading={submitting}
              data-testid='save-referral'>
              {isEdit ? 'Save' : onInsurance ? 'Submit' : 'Create Referral'}
            </Button>
          )}
        </Actions>
      </Form>
    </StandardModal>
  );
}

// 24px between sections and 16px above the first one (the shared modal body already contributes
// 24px of top padding, and the mock wants 40px clear of the header). Measured from Figma
// 10317:3729 rather than eyeballed — 16px between sections read as cramped.
const Form = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  gap: 24px;
  padding-top: 16px;
`;

const Panel = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
`;

const Lead = styled(Body)`
  display: block;
`;

// Segmented control, sized from the mock: a 40px track with the selected segment inset 4px
// inside it. The selection reads as a tinted panel, not a solid button — a solid fill here
// competes with the modal's actual submit button for "the thing to press".
const Methods = styled.div`
  display: flex;
  gap: 4px;
  height: 40px;
  margin-top: 8px;
  padding: 3px 4px;
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

const MethodTab = styled.button<{ $active: boolean }>`
  flex: 1;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  // Both segments stay at 400. Special Gothic ships one physical weight (see index.css), so
  // anything heavier is the browser's faux bold — the selection is carried by colour and fill,
  // which is what the mock distinguishes them by anyway.
  font-weight: 400;
  background: ${({ $active }) => ($active ? colors.blue10 : 'transparent')};
  color: ${({ $active }) => ($active ? colors.blue100 : colors.black100)};
`;

const StackedFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const CoverageNote = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: ${colors.black75};
`;

const Fields = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const Field = styled.div`
  min-width: 200px;
  flex: 1;
`;

const SectionTitle = styled(BodySmall)`
  display: block;
  margin-top: 16px;
  font-weight: 500;
  color: ${colors.black75};
`;

const Optional = styled.span`
  font-weight: 400;
  color: ${colors.black50};
`;

const RerunNote = styled(BodySmall)`
  display: block;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${colors.blue25};
  background: ${colors.blue5};
  color: ${colors.black75};
`;

const Toggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-weight: 500;
`;

const DropZone = styled.div`
  margin-top: 8px;
`;

const CurrentPacket = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: ${colors.black75};
`;

const ReplaceWarning = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: ${colors.orangeHover};
  font-weight: 500;
`;

const HelpText = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.red100};
`;
